<template>
  <v-dialog
    @keydown.esc="balanceDialog=false"
    max-width="500"
    v-model="balanceDialog"
    persistent
  >
    <template v-slot:activator="{ on }">
      <div v-if="overlay">
        <v-progress-circular color="primary" indeterminate />
      </div>
      <div v-else>
        <v-btn
          v-if="paymentData && paymentData.source"
          depressed
          rounded
          small
          color="primary"
          v-on="on"
        >
          Aumentar saldo
        </v-btn>
        <div v-else>
          No tienes información de pago
        </div>
      </div>
    </template>

    <v-card class="py-4">
      <v-card-text class="px-4 py-0">
        <v-row>
          <v-col cols="12">
            <div class="subtitle-1">
              ¿Cuánto saldo deseas aumentar en tu cuenta de Eato?
            </div>
          </v-col>
          <v-col cols="12">
            <div class="d-flex justify-space-between">
              <div class="black--text">
                Saldo disponible:
              </div>
              <div class="primary--text">
                {{ availableBalance | toFixNumber(2) | numberWithComma | prefix('$') }}
              </div>
            </div>
            <div
              v-if="showPurchaseInformation"
              class="d-flex justify-space-between">
              <div class="black--text">
                Compra total:
              </div>
              <div class="black--text">
                {{ totalPurchase | toFixNumber(2) | numberWithComma | prefix('$') }}
              </div>
            </div>
          </v-col>
          <v-col cols="12">
            <v-divider class="mt-4pb-4" />
          </v-col>
          <v-col
            v-if="showPurchaseInformation"
            cols="12">
            <div class="d-flex justify-space-between">
              <div class="black--text">
                Monto sugerido a depositar para realizar la compra:
              </div>
              <div class="primary--text">
                {{ suggestedAmount | toFixNumber(2) | numberWithComma | prefix('$') }}
              </div>
            </div>
            <div class="d-flex justify-space-between">
              <div class="grey--text">
                <div>
                  Incluye comisión del {{ commission }}%
                </div>
                <div>
                  Incluye impuesto del {{ this.taxes.iva }}% en la comisión.
                </div>
              </div>
            </div>
          </v-col>
          <v-col
            v-if="!showPurchaseInformation"
            cols="12">
            <div class="d-flex justify-space-between">
              <div class="black--text">
                Monto disponible para compras:
              </div>
              <div class="primary--text">
                {{ amountAvailable | toFixNumber(2) | numberWithComma | prefix('$') }}
              </div>
            </div>
            <div class="d-flex justify-space-between">
              <div class="grey--text">
                Se cobra una comisión de {{ amountCommission | toFixNumber(2) | numberWithComma | prefix('$') }}
              </div>
            </div>
          </v-col>
          <v-col cols="12">
            <div>
              <v-text-field
                v-model.number="amount"
                type="number"
                label="Monto a depositar:"
                :rules="rules.notEnoughBalance"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="d-flex justify-end px-4">
        <v-btn
          outlined
          rounded
          small
          depressed
          color="primary"
          @click="closeBalanceDialog">
          Cancelar
        </v-btn>
        <v-btn
          :disabled="this.amount < 1000"
          rounded
          depressed
          small
          color="primary"
          @click="addBalance"
        >
          Aceptar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog
      @keydown.esc="confirmDialog=false"
      max-width="500"
      persistent
      v-model="confirmDialog"
    >
      <v-card class="py-4">
        <v-card-text class="px-4 py-0">
          <div class="pb-4">
            <div>
              Realiza un depósito o una transferencia a la CLABE:
            </div>
            <div>
              <b>{{ restaurant.paymentData && restaurant.paymentData.source ? restaurant.paymentData.source.clabe : '' }}</b>
            </div>
            <div>
              El depósito o transferencia debe ser <b>${{ amount | toFixNumber(2) | numberWithComma }}</b>
            </div>
          </div>
          <v-alert
            color="blue-grey"
            dark
            icon="mdi-alert-circle"
          >
            Para que tu saldo se abone correctamente, deberás depositar la cantidad exacta que ingresaste.
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-4 d-flex justify-end">
          <v-btn
            color="primary"
            dark
            small
            depressed
            @click="function () {
              balanceDialog = false
              confirmDialog = false
            }"
          >
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import Balance from '@/models/Balance'

export default {
  props: {
    showPurchaseInformation: {
      type: Boolean,
      default: false
    },
    commission: {
      type: Number,
      default: 0
    },
    availableBalance: {
      type: Number,
      default: 0
    },
    totalPurchase: {
      type: Number,
      default: 0
    },
    paymentData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      balanceModel: null,
      balanceDialog: false,
      confirmDialog: false,
      overlay: false,
      amount: 0,
      taxes: {
        iva: 16
      },
      rules: {
        notEnoughBalance: [
          v => v >= Number(this.suggestedAmount) || 'El saldo no es suficiente para completar la compra',
          v => v >= Number(1000) || 'Monto mínimo a depositar: $1000'
        ]
      }
    }
  },
  computed: {
    totalDifference () {
      const totalDifference = Math.max(0, this.totalPurchase - this.availableBalance)
      return Number(totalDifference.toFixed(2))
    },
    suggestedAmount () {
      const suggestedAmount = this.totalDifference + this.totalCommission
      return Number((suggestedAmount).toFixed(2))
    },
    amountCommission () {
      const amountCommission = (this.amount * (this.commission / 100)).toFixed(2)
      return this.ammountPlusTaxes(amountCommission)
    },
    amountAvailable () {
      return Number((this.amount - this.amountCommission).toFixed(2))
    },
    totalCommission () {
      const totalCommission = Number(this.totalDifference * (this.commission / 100)).toFixed(2)
      return this.ammountPlusTaxes(totalCommission)
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    }
  },
  mounted () {
    this.balanceModel = new Balance({ restaurant: this.restaurant._id })
  },
  watch: {
    balanceDialog (val) {
      if (val) this.setDefaultAmount()
    },
    restaurant (val) {
      this.balanceModel = new Balance({ restaurant: this.restaurant._id })
    }
  },
  methods: {
    ammountPlusTaxes (amount) {
      const ammountPlusTaxes = Number(amount) + (Number(amount) * (this.taxes.iva / 100))
      return Number(ammountPlusTaxes.toFixed(2))
    },
    async addBalance () {
      this.overlay = true
      try {
        await this.balanceModel.add({ amount: this.amount })
        this.$toasted.show('Transacción completada', { duration: 3000 })
        this.$emit('update')
        this.confirmDialog = true
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    closeBalanceDialog () {
      this.balanceDialog = false
      this.setDefaultAmount()
    },
    closeAllDialogs () {
      this.balanceDialog = false
      this.confirmDialog = false
      this.setDefaultAmount()
    },
    setDefaultAmount () {
      this.amount = Number(this.suggestedAmount)
    }
  }
}
</script>
